if (!customElements.get('native-content-rail')) {
  customElements.define('native-content-rail', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.track = this.querySelector('[data-rail-track]');
      this.controls = this.querySelector('[data-rail-controls]');
      this.dots = this.querySelector('[data-rail-dots]');
      if (!this.track || !this.controls || !this.dots) return;
      this.previous = this.querySelector('[data-rail-previous]');
      this.next = this.querySelector('[data-rail-next]');
      this.previous.addEventListener('click', () => this.move(-1), { signal });
      this.next.addEventListener('click', () => this.move(1), { signal });
      this.dots.addEventListener('click', event => {
        const button = event.target.closest('button[data-position]');
        if (button && this.dots.contains(button)) {
          if (this.isRelatedRail) this.goToCard(Array.from(this.dots.children).indexOf(button) * this.cardStep);
          else this.go(Number(button.dataset.position));
        }
      }, { signal });
      this.isRelatedRail = Boolean(this.closest('.native-related-articles'));
      if (this.isRelatedRail) {
        ['wheel', 'pointerdown', 'touchstart', 'keydown'].forEach(type => {
          this.track.addEventListener(type, () => {
            this.relatedNavigation = false;
            clearTimeout(this.relatedScrollTimer);
          }, { passive: true, signal });
        });
      }
      this.track.addEventListener('scroll', () => {
        if (this.isRelatedRail) {
          clearTimeout(this.relatedScrollTimer);
          this.relatedScrollTimer = setTimeout(() => {
            if (!this.relatedNavigation) this.currentCard = this.visibleCardIndex();
            this.relatedNavigation = false;
            this.update();
          }, 100);
        }
        this.update();
      }, { passive: true, signal });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.measure();
    }
    measure() {
      const cards = Array.from(this.track.children).filter(card => card.getBoundingClientRect().width > 0);
      const active = matchMedia(`(min-width:${this.dataset.minWidth || 0}px)`).matches;
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const first = cards[0];
      const distance = cards[1] && first ? cards[1].offsetLeft - first.offsetLeft : this.track.clientWidth;
      const step = Math.max(1, Number(this.dataset.step || 1)) * Math.max(1, distance);
      if (this.isRelatedRail) {
        this.cards = cards;
        this.cardStep = Math.max(1, Number(this.dataset.step || 1));
        this.cardsShown = Math.max(1, Math.floor(this.track.clientWidth / Math.max(1, distance)));
        if (!this.relatedNavigation) this.currentCard = this.visibleCardIndex();
      }
      this.positions = [0];
      if (active && maximum > 1) {
        for (let position = step; position < maximum - 1; position += step) this.positions.push(position);
        this.positions.push(maximum);
      }
      this.controls.hidden = !active || maximum <= 1;
      this.dots.replaceChildren();
      this.positions.forEach((position, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', `Go to slide ${index + 1}`);
        button.dataset.position = position;
        this.dots.append(button);
      });
      this.update();
    }
    update() {
      if (!this.positions) return;
      const x = Math.max(0, this.track.scrollLeft);
      this.activeIndex = this.isRelatedRail
        ? Math.min(this.positions.length - 1, Math.ceil((this.currentCard || 0) / this.cardStep))
        : this.positions.reduce((best, value, index) =>
          Math.abs(value - x) < Math.abs(this.positions[best] - x) ? index : best, 0);
      this.previous.disabled = x <= 1;
      this.next.disabled = x >= this.positions[this.positions.length - 1] - 1;
      Array.from(this.dots.children).forEach((button, index) => {
        button.setAttribute('aria-current', index === this.activeIndex ? 'true' : 'false');
      });
    }
    move(direction) {
      if (this.isRelatedRail) {
        const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        // A clamped last page starts between card positions; rebase from the
        // visible cards before stepping backwards, preserving odd card indexes.
        if (direction < 0 && this.track.scrollLeft >= maximum - 1) this.currentCard = this.visibleCardIndex();
        const target = direction < 0
          ? Math.max(0, (this.currentCard || 0) - this.cardStep)
          : Math.min(this.cards.length - this.cardStep, (this.currentCard || 0) + this.cardStep);
        this.goToCard(target);
        return;
      }
      const index = Math.max(0, Math.min(this.positions.length - 1, this.activeIndex + direction));
      this.go(this.positions[index]);
    }
    visibleCardIndex() {
      if (!this.cards?.length) return 0;
      const track = this.track.getBoundingClientRect();
      const visible = this.cards.map((card, index) => ({ rect: card.getBoundingClientRect(), index }))
        .filter(({ rect }) => rect.right > track.left && rect.left < track.right);
      let index = visible[0]?.index ?? 0;
      if (visible.length >= this.cardsShown + 2 || index > 0) index += 1;
      return index;
    }
    goToCard(index) {
      if (!this.cards?.length) return;
      this.currentCard = Math.max(0, Math.min(this.cards.length - 1, index));
      this.relatedNavigation = true;
      // A repeated dot can request the current position and emit no scroll.
      // Give that navigation a bounded lifetime; real scroll events renew it.
      clearTimeout(this.relatedScrollTimer);
      this.relatedScrollTimer = setTimeout(() => { this.relatedNavigation = false; }, 100);
      this.update();
      // Request the original card offset. CSS scroll padding supplies the
      // partial-card snap position, and the browser clamps the final page.
      this.go(this.cards[this.currentCard].offsetLeft - this.cards[0].offsetLeft);
    }
    go(left) {
      this.track.scrollTo({ left, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      clearTimeout(this.relatedScrollTimer);
      this.relatedNavigation = false;
      this.controller = null;
    }
  });
}
